import { colors, typography, spacing } from '@mehilainen/mds-tokens';
import { formHelperTextClasses, inputBaseClasses, inputLabelClasses, outlinedInputClasses, svgIconClasses, } from '@mui/material';
import { mdsStyleClasses } from '../../mdsStyleClasses';
import { getInputLabelTransformStyle } from './utils';
const { baseTypography } = typography;
//Common styles for all MUI input fields
export const getThemeFormElements = (tokens) => {
    var _a;
    const inputTokens = tokens.uiElementInputFields;
    const useStandardLabel = tokens.values.inputLabelPlacement === 'standard';
    return {
        MuiFormControl: {
            defaultProps: {
                variant: 'outlined',
                size: 'small',
                className: useStandardLabel
                    ? mdsStyleClasses.standardTextField
                    : mdsStyleClasses.notchedTextField,
            },
            styleOverrides: {
                root: {
                    // NOTE: MUI Textfield does not allow variants so MDS uses custom variants via classes
                    // MDS Standard Textfield styles
                    [`&.${mdsStyleClasses.standardTextField}`]: {
                        // input label
                        [`& > .${inputLabelClasses.outlined}`]: {
                            position: 'unset',
                            paddingBottom: '4px',
                            WebkitTransform: 'unset',
                            MozTransform: 'unset',
                            msTransform: 'unset',
                            transform: 'unset',
                            fontSize: inputTokens.sizeMedium.labelFontSize,
                            fontWeight: inputTokens.sizeMedium.labelFontWeight,
                            lineHeight: baseTypography.height.Medium,
                            // small styles
                            [`&.${inputLabelClasses.sizeSmall}`]: {
                                fontSize: inputTokens.sizeSmall.labelFontSize,
                                fontWeight: inputTokens.sizeSmall.labelFontWeight,
                            },
                            // NOTE: This override cannot exist in input since the MUI used CSS selector is very specific AND contains the !important too
                            [`&[data-shrink=false]+.${inputBaseClasses.formControl}  input::-webkit-input-placeholder`]: { opacity: '1 !important' },
                        },
                        // // input
                        [`& > .${outlinedInputClasses.root}`]: {
                            // fieldset
                            [`& > .${outlinedInputClasses.notchedOutline} > legend`]: {
                                // Prevent notch placecholder that breaks border for notched label
                                maxWidth: 0,
                            },
                        },
                    },
                    // MDS Notched Textfield styles
                    [`&.${mdsStyleClasses.notchedTextField}`]: {
                        // input label
                        [`& > .${inputLabelClasses.outlined}`]: Object.assign(Object.assign({ fontSize: inputTokens.sizeMedium.notchedLabelFontSize, fontWeight: inputTokens.sizeMedium.notchedLabelFontWeight, lineHeight: baseTypography.height.Small }, getInputLabelTransformStyle('medium', true, tokens)), { [`&:not(.${inputLabelClasses.shrink})`]: Object.assign({ fontSize: inputTokens.sizeMedium.contentFontSize, fontWeight: baseTypography.weight.Regular, lineHeight: baseTypography.height.Small }, getInputLabelTransformStyle('medium', false, tokens)), 
                            // small styles
                            [`&.${inputLabelClasses.sizeSmall}`]: Object.assign(Object.assign({ fontSize: inputTokens.sizeSmall.notchedLabelFontSize, fontWeight: inputTokens.sizeSmall.notchedLabelFontWeight, lineHeight: baseTypography.height.Small }, getInputLabelTransformStyle('small', true, tokens)), { [`&:not(.${inputLabelClasses.shrink})`]: Object.assign({ fontSize: inputTokens.sizeSmall.contentFontSize, fontWeight: baseTypography.weight.Regular, lineHeight: baseTypography.height.Small }, getInputLabelTransformStyle('small', false, tokens)) }) }),
                        // input
                        [`& > .${outlinedInputClasses.root}`]: {
                            // fieldset
                            [`& > .${outlinedInputClasses.notchedOutline}`]: {
                                paddingLeft: ((_a = inputTokens.common.paddingLeft) !== null && _a !== void 0 ? _a : 16) - 5 + 'px',
                                '& > legend': {
                                    fontSize: inputTokens.sizeMedium.notchedLabelFontSize,
                                    fontWeight: inputTokens.sizeMedium.notchedLabelFontWeight,
                                    lineHeight: baseTypography.height.Small,
                                },
                            },
                            // small styles
                            [`&.${inputBaseClasses.sizeSmall}`]: {
                                // fieldset > legend
                                [`& > .${outlinedInputClasses.notchedOutline} > legend`]: {
                                    fontSize: inputTokens.sizeSmall.notchedLabelFontSize,
                                    fontWeight: inputTokens.sizeSmall.notchedLabelFontWeight,
                                    lineHeight: baseTypography.height.Small,
                                },
                            },
                        },
                    },
                },
            },
        },
        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
                size: 'small',
                minRows: 3,
                maxRows: 8,
                className: useStandardLabel
                    ? mdsStyleClasses.standardTextField
                    : mdsStyleClasses.notchedTextField,
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    color: inputTokens.common.labelColor,
                    '&.Mui-focused:not(.Mui-error)': {
                        color: inputTokens.common.labelFocusedColor,
                    },
                    '&.Mui-disabled': {
                        color: colors.Gray400,
                    },
                },
                filled: {
                    color: inputTokens.common.labelFilledColor,
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    lineHeight: baseTypography.height.Medium,
                    '&::placeholder': {
                        opacity: 1,
                        color: colors.Gray600,
                    },
                },
                root: {
                    [`:not(.${inputBaseClasses.multiline})`]: {
                        minHeight: inputTokens.sizeMedium.height,
                        [`&.${inputBaseClasses.sizeSmall}`]: {
                            minHeight: inputTokens.sizeSmall.height,
                        },
                        // For some reason error- css selector was not working
                        '&.Mui-error': {
                            [`& .${svgIconClasses.root}`]: {
                                color: colors.Error500,
                            },
                        },
                    },
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    color: colors.Gray600,
                    fontSize: inputTokens.sizeMedium.assistiveTextSize,
                    fontWeight: inputTokens.sizeMedium.assistiveTextWeight,
                    lineHeight: baseTypography.height.Medium,
                    marginLeft: 0,
                    marginTop: spacing[200].px,
                    [`&.${formHelperTextClasses.sizeSmall}`]: {
                        fontSize: inputTokens.sizeSmall.assistiveTextSize,
                        fontWeight: inputTokens.sizeSmall.assistiveTextWeight,
                    },
                    [`&:not(.Mui-error).Mui-focused`]: {
                        color: colors.Primary500,
                    },
                },
            },
        },
        MuiInput: {
            styleOverrides: {
                root: {
                    '&:hover:not(.Mui-disabled):before': {
                        borderColor: colors.Primary500,
                    },
                    '&::before': {
                        borderColor: inputTokens.common.borderColor,
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    fontSize: inputTokens.sizeMedium.contentFontSize,
                    lineHeight: baseTypography.height.Small,
                    backgroundColor: colors.White,
                    color: colors.Gray900,
                    borderColor: inputTokens.common.borderColor,
                    borderRadius: inputTokens.common.borderRadius,
                    [`&.${inputBaseClasses.sizeSmall}`]: {
                        fontSize: inputTokens.sizeSmall.contentFontSize,
                    },
                    // textarea stylings
                    [`&.${inputBaseClasses.multiline}`]: {
                        padding: `${spacing[400].px} ${spacing[500].px}`,
                        [`& .${inputBaseClasses.input}`]: {
                            padding: 0,
                        },
                    },
                    '&.Mui-error': {
                        borderColor: colors.Error500,
                        color: colors.Error500,
                        ':not(:hover):not(active)': {
                            backgroundColor: inputTokens.common.errorBackground,
                        },
                    },
                    '&:hover:not(.Mui-error):not(.Mui-disabled)': {
                        borderColor: colors.Primary500,
                    },
                    '&.Mui-focused:not(.Mui-error):not(.Mui-disabled)': {
                        borderColor: colors.Primary500,
                    },
                    '&.Mui-disabled': {
                        borderColor: colors.Gray300,
                        backgroundColor: colors.Gray100,
                    },
                },
                // NOTE: No idea why this exists in dom if notched is false, but this actually contains all the needed border rules
                notchedOutline: {
                    borderColor: 'inherit !important', // Otherwise default bordercolors just override custom ones given at root
                    //borderWidth: '1px !important', // Active input has width 2 px on default
                },
                input: {
                    padding: `${spacing[300].px} ${inputTokens.common.paddingLeft}px`,
                },
            },
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: {
                    color: colors.Gray600,
                },
            },
        },
    };
};
